import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import MagneticButton from './MagneticButton';
import sparkLogo from '../assets/spark-logo.png';
// Import the component
import { CollegeAddon } from './CollegeAddon'; 

const navLinks = [
  { name: 'Explore', href: '/info#explore', id: 'explore' },
  { name: 'Community', href: '/info/community', id: 'community' },
  { name: 'Get Started', href: '/info#get-started', id: 'get-started' },
  { name: 'Contact', href: '/info/contact', id: 'contact' },
];

export const Header = ({ minimal = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      setIsScrolled(currentScrollY > 50);

      // Hide header when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleNavClick = (e, href, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    window.history.pushState(null, '', href);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#0a0c10]/95 backdrop-blur-2xl py-3 border-transparent shadow-none' : 'bg-transparent py-4 border-transparent shadow-none'
        } ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}
        initial={{ y: -100 }}
        animate={{ y: isVisible ? 0 : -100 }}
        transition={{ duration: 0.3 }}
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          {minimal ? (
            <a href="/" className="flex items-center gap-2 group block hover:scale-105 transition-transform">
              <img src={sparkLogo} alt="Spark Logo" className="h-8 w-auto" />
            </a>
          ) : (
            <motion.a
              href="/"
              className="flex items-center gap-2 group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img src={sparkLogo} alt="Spark Logo" className="h-8 w-auto" />
            </motion.a>
          )}

          {!minimal && (
            <>
              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-8">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href, link.id)}
                    className="relative text-sp-text-secondary hover:text-sp-cyan transition-colors duration-200 font-medium after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[2px] after:bg-sp-cyan after:transition-all after:duration-300 hover:after:w-full"
                  >
                    {link.name}
                  </a>
                ))}
              </nav>

              {/* CTA Button */}
              <div className="hidden md:block">
                <MagneticButton>
                  <motion.a
                    href="/login"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-block"
                  >
                    <Button
                      size="sm"
                      className="bg-sp-cyan hover:bg-sp-cyan/80 text-black font-semibold rounded-full hover:shadow-[0_0_20px_rgba(11,217,229,0.5)] transition-all duration-300"
                    >
                      Sign In
                    </Button>
                  </motion.a>
                </MagneticButton>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden text-sp-cyan p-2"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </>
          )}
        </div>
      </motion.header>

      {/* Mobile Menu */}
      {!minimal && isMobileMenuOpen && (
        <motion.div
          className="fixed inset-0 z-40 md:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-sp-bg/95 backdrop-blur-xl" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative h-full flex flex-col items-center justify-center gap-8">
            
            {/* Standard Nav Links */}
            {navLinks.map((link, index) => (
              <motion.a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href, link.id)}
                className="relative text-2xl font-bold text-sp-text hover:text-sp-cyan transition-colors duration-200 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[2px] after:bg-sp-cyan after:transition-all after:duration-300 hover:after:w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {link.name}
              </motion.a>
            ))}

            {/* NEW: Add Your Campus (Mobile Only) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }} // Slightly before the button
            >
              <CollegeAddon 
                customTrigger={
                  <span className="text-2xl font-bold text-sp-text hover:text-sp-cyan transition-colors duration-200 cursor-pointer relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[2px] after:bg-sp-cyan after:transition-all after:duration-300 hover:after:w-full">
                    Add Your College
                  </span>
                } 
              />
            </motion.div>

            {/* Sign In Button */}
            <motion.a
              href="/login"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Button
                size="lg"
                className="bg-sp-cyan hover:bg-sp-cyan/80 text-black font-semibold rounded-full px-12"
              >
                Sign In
              </Button>
            </motion.a>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default Header;




